/**
 * QuestionnaireRenderer — Multi-Step Guided ESG Data Collection Form
 *
 * Renders a questionnaire template into an interactive, multi-step form.
 * Features:
 *   - Fetches template structure + prepopulated data from API
 *   - Renders each template section as a navigable step
 *   - Field types: number, boolean, select, text, percentage
 *   - Real-time validation (min/max from template rules)
 *   - Progress bar (required fields filled / total required)
 *   - Save Draft on every step, Previous/Next navigation
 *   - Final "Review & Submit" step with summary
 *   - Contextual help tooltips per indicator
 *   - Pre-populated fields labelled with source period
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  Check,
  Save,
  Send,
  Info,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { clsx } from 'clsx';
import { apiClient } from '@/api/client';

// ─── Types ──────────────────────────────────────────────────────

interface ValidationRule {
  min?: number;
  max?: number;
  required?: boolean;
  options?: string[];
}

interface TemplateIndicator {
  id: number;
  name: string;
  code?: string;
  description?: string;
  type: 'number' | 'boolean' | 'select' | 'text' | 'percentage';
  unit?: string;
  validation?: ValidationRule;
  required?: boolean;
}

interface TemplateSection {
  id: number;
  name: string;
  description?: string;
  order?: number;
  indicators: TemplateIndicator[];
}

interface TemplateDetail {
  id: number;
  name: string;
  template_type: string;
  version?: string;
  sections: TemplateSection[];
}

interface PrepopulatedField {
  indicator_id: number;
  value: unknown;
  source_period?: string;
  source_year?: number;
  source_quarter?: number;
}

interface FieldError {
  indicatorId: number;
  message: string;
}

export interface QuestionnaireRendererProps {
  templateId: number;
  fundId: number;
  investeeId: number;
  existingData?: Record<string, unknown>;
  onSubmit: (data: Record<string, unknown>) => void;
  onSaveDraft: (data: Record<string, unknown>) => void;
}

// ─── Main Component ─────────────────────────────────────────────

export function QuestionnaireRenderer({
  templateId,
  fundId,
  investeeId,
  existingData,
  onSubmit,
  onSaveDraft,
}: QuestionnaireRendererProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<Record<string, unknown>>(existingData ?? {});
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);
  const [prepopulatedIndicators, setPrepopulatedIndicators] = useState<Set<number>>(new Set());
  const [prepopSources, setPrepopSources] = useState<Record<number, string>>({});
  const [isSaving, setIsSaving] = useState(false);

  // Fetch template structure
  const templateQuery = useQuery<TemplateDetail>({
    queryKey: ['ic', 'esg', 'template', templateId],
    queryFn: async () => {
      const res = await apiClient.get(`/api/v1/impact-core/esg/templates/${templateId}`);
      return res.data?.data ?? res.data;
    },
    staleTime: 120_000,
  });

  // Fetch prepopulated data
  const prepopQuery = useQuery<PrepopulatedField[]>({
    queryKey: ['ic', 'esg', 'prepopulate', investeeId, fundId],
    queryFn: async () => {
      const res = await apiClient.get(
        `/api/v1/impact-core/esg/submissions/prepopulate?investee_id=${investeeId}&fund_id=${fundId}`,
      );
      return res.data?.data ?? res.data ?? [];
    },
    staleTime: 120_000,
  });

  // Apply prepopulated data when loaded
  useEffect(() => {
    if (prepopQuery.data && prepopQuery.data.length > 0) {
      const newData: Record<string, unknown> = { ...formData };
      const prepopIds = new Set<number>();
      const sources: Record<number, string> = {};

      for (const field of prepopQuery.data) {
        const key = `indicator_${field.indicator_id}`;
        // Only prepopulate if user hasn't already entered a value
        if (newData[key] === undefined || newData[key] === '' || newData[key] === null) {
          newData[key] = field.value;
          prepopIds.add(field.indicator_id);
          if (field.source_period) {
            sources[field.indicator_id] = field.source_period;
          } else if (field.source_year) {
            sources[field.indicator_id] = field.source_quarter
              ? `Q${field.source_quarter} ${field.source_year}`
              : `${field.source_year}`;
          }
        }
      }

      setFormData(newData);
      setPrepopulatedIndicators(prepopIds);
      setPrepopSources(sources);
    }
    // Only run when prepopulated data loads, not on every formData change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prepopQuery.data]);

  const sections = useMemo(() => {
    if (!templateQuery.data?.sections) return [];
    return [...templateQuery.data.sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [templateQuery.data]);

  // Total steps = sections + review
  const totalSteps = sections.length + 1;
  const isReviewStep = currentStep === sections.length;

  // ─── Field Value Management ───────────────────────────────────

  const setFieldValue = useCallback((indicatorId: number, value: unknown) => {
    setFormData((prev) => ({
      ...prev,
      [`indicator_${indicatorId}`]: value,
    }));
    // Clear error for this field
    setFieldErrors((prev) => prev.filter((e) => e.indicatorId !== indicatorId));
  }, []);

  const getFieldValue = useCallback(
    (indicatorId: number): unknown => {
      return formData[`indicator_${indicatorId}`] ?? '';
    },
    [formData],
  );

  // ─── Validation ───────────────────────────────────────────────

  const validateField = useCallback(
    (indicator: TemplateIndicator): string | null => {
      const value = getFieldValue(indicator.id);
      const isRequired = indicator.required ?? indicator.validation?.required ?? false;

      if (isRequired && (value === '' || value === null || value === undefined)) {
        return 'This field is required';
      }

      if (value === '' || value === null || value === undefined) return null;

      const rules = indicator.validation;
      if (!rules) return null;

      if (indicator.type === 'number' || indicator.type === 'percentage') {
        const numValue = Number(value);
        if (isNaN(numValue)) return 'Must be a valid number';
        if (rules.min !== undefined && numValue < rules.min) return `Minimum value is ${rules.min}`;
        if (rules.max !== undefined && numValue > rules.max) return `Maximum value is ${rules.max}`;
        if (indicator.type === 'percentage') {
          if (numValue < 0) return 'Percentage must be 0 or above';
          if (numValue > 100) return 'Percentage must be 100 or below';
        }
      }

      return null;
    },
    [getFieldValue],
  );

  const validateSection = useCallback(
    (sectionIndex: number): FieldError[] => {
      const section = sections[sectionIndex];
      if (!section) return [];

      const errors: FieldError[] = [];
      for (const indicator of section.indicators) {
        const error = validateField(indicator);
        if (error) {
          errors.push({ indicatorId: indicator.id, message: error });
        }
      }
      return errors;
    },
    [sections, validateField],
  );

  // ─── Progress Calculation ─────────────────────────────────────

  const progress = useMemo(() => {
    let totalRequired = 0;
    let filledRequired = 0;

    for (const section of sections) {
      for (const indicator of section.indicators) {
        const isRequired = indicator.required ?? indicator.validation?.required ?? false;
        if (isRequired) {
          totalRequired++;
          const value = formData[`indicator_${indicator.id}`];
          if (value !== undefined && value !== '' && value !== null) {
            filledRequired++;
          }
        }
      }
    }

    return totalRequired > 0 ? Math.round((filledRequired / totalRequired) * 100) : 100;
  }, [formData, sections]);

  // Track which sections are complete
  const sectionComplete = useMemo(() => {
    return sections.map((_section, idx) => {
      const errors = validateSection(idx);
      return errors.length === 0;
    });
  }, [sections, validateSection]);

  // ─── Navigation ───────────────────────────────────────────────

  const goNext = useCallback(() => {
    if (!isReviewStep) {
      const errors = validateSection(currentStep);
      setFieldErrors(errors);
      // Allow navigation even with errors (soft validation)
    }
    if (currentStep < totalSteps - 1) {
      setCurrentStep((s) => s + 1);
      window.scrollTo(0, 0);
    }
  }, [currentStep, totalSteps, isReviewStep, validateSection]);

  const goPrevious = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
      setFieldErrors([]);
      window.scrollTo(0, 0);
    }
  }, [currentStep]);

  const goToStep = useCallback(
    (step: number) => {
      if (step >= 0 && step < totalSteps) {
        setCurrentStep(step);
        setFieldErrors([]);
        window.scrollTo(0, 0);
      }
    },
    [totalSteps],
  );

  // ─── Actions ──────────────────────────────────────────────────

  const handleSaveDraft = useCallback(async () => {
    setIsSaving(true);
    try {
      onSaveDraft(formData);
    } finally {
      setIsSaving(false);
    }
  }, [formData, onSaveDraft]);

  const handleSubmit = useCallback(() => {
    // Validate all sections before submit
    const allErrors: FieldError[] = [];
    for (let i = 0; i < sections.length; i++) {
      allErrors.push(...validateSection(i));
    }

    if (allErrors.length > 0) {
      setFieldErrors(allErrors);
      // Find the first section with an error and navigate to it
      for (let i = 0; i < sections.length; i++) {
        const sectionErrors = validateSection(i);
        if (sectionErrors.length > 0) {
          setCurrentStep(i);
          break;
        }
      }
      return;
    }

    onSubmit(formData);
  }, [formData, sections, validateSection, onSubmit]);

  // ─── Loading / Error States ───────────────────────────────────

  if (templateQuery.isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2 className="h-6 w-6 animate-spin text-ic-accent" />
          <span className="text-xs text-ic-text-tertiary">Loading questionnaire...</span>
        </div>
      </div>
    );
  }

  if (templateQuery.isError || !templateQuery.data) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex flex-col items-center gap-2">
          <AlertCircle className="h-5 w-5 text-red-400" />
          <p className="text-xs text-red-400">Failed to load questionnaire template.</p>
        </div>
      </div>
    );
  }

  if (sections.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-xs text-ic-text-tertiary">This template has no sections configured.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Progress Bar */}
      <div className="rounded-lg border border-ic-border bg-ic-card p-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs font-medium text-ic-text-secondary">Overall Progress</span>
          <span className="text-xs font-mono text-ic-text-tertiary">{progress}%</span>
        </div>
        <div className="h-2 w-full overflow-hidden rounded-full bg-slate-700/50">
          <div
            className={clsx(
              'h-full rounded-full transition-[width] duration-500',
              progress >= 80 ? 'bg-emerald-500' : progress >= 50 ? 'bg-amber-500' : 'bg-blue-500',
            )}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>

      {/* Step Navigation */}
      <div className="rounded-lg border border-ic-border bg-ic-card p-4">
        <div className="flex items-center gap-1 overflow-x-auto">
          {sections.map((section, idx) => (
            <button
              key={section.id}
              onClick={() => goToStep(idx)}
              className={clsx(
                'flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-xs font-medium transition-colors',
                currentStep === idx
                  ? 'bg-ic-accent/15 text-ic-accent border border-ic-accent/30'
                  : sectionComplete[idx]
                    ? 'text-emerald-400 hover:bg-emerald-500/10'
                    : 'text-ic-text-tertiary hover:bg-ic-hover/50 hover:text-ic-text-secondary',
              )}
            >
              {sectionComplete[idx] && currentStep !== idx ? (
                <Check className="h-3 w-3" />
              ) : (
                <span className="flex h-4 w-4 items-center justify-center rounded-full bg-slate-700/50 text-[9px]">
                  {idx + 1}
                </span>
              )}
              {section.name}
            </button>
          ))}
          <button
            onClick={() => goToStep(sections.length)}
            className={clsx(
              'flex items-center gap-1.5 whitespace-nowrap rounded-md px-3 py-2 text-xs font-medium transition-colors',
              isReviewStep
                ? 'bg-ic-accent/15 text-ic-accent border border-ic-accent/30'
                : 'text-ic-text-tertiary hover:bg-ic-hover/50 hover:text-ic-text-secondary',
            )}
          >
            <Send className="h-3 w-3" />
            Review & Submit
          </button>
        </div>
      </div>

      {/* Form Content */}
      {!isReviewStep ? (
        <SectionForm
          section={sections[currentStep]!}
          formData={formData}
          fieldErrors={fieldErrors}
          prepopulatedIndicators={prepopulatedIndicators}
          prepopSources={prepopSources}
          onFieldChange={setFieldValue}
          getFieldValue={getFieldValue}
        />
      ) : (
        <ReviewStep
          sections={sections}
          formData={formData}
          prepopulatedIndicators={prepopulatedIndicators}
          getFieldValue={getFieldValue}
          fieldErrors={fieldErrors}
        />
      )}

      {/* Navigation Buttons */}
      <div className="flex items-center justify-between rounded-lg border border-ic-border bg-ic-card p-4">
        <div className="flex items-center gap-2">
          {currentStep > 0 && (
            <button
              onClick={goPrevious}
              className="flex items-center gap-1 rounded-md border border-ic-border px-3 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Previous
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={handleSaveDraft}
            disabled={isSaving}
            className="flex items-center gap-1 rounded-md border border-ic-border px-3 py-2 text-xs font-medium text-ic-text-secondary hover:bg-ic-hover transition-colors disabled:opacity-50"
          >
            {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save Draft
          </button>

          {isReviewStep ? (
            <button
              onClick={handleSubmit}
              className="flex items-center gap-1 rounded-md bg-ic-accent px-4 py-2 text-xs font-semibold text-white hover:bg-ic-accent-hover transition-colors"
            >
              <Send className="h-3.5 w-3.5" />
              Submit
            </button>
          ) : (
            <button
              onClick={goNext}
              className="flex items-center gap-1 rounded-md bg-ic-accent px-3 py-2 text-xs font-medium text-white hover:bg-ic-accent-hover transition-colors"
            >
              Next
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Section Form ────────────────────────────────────────────────

interface SectionFormProps {
  section: TemplateSection;
  formData: Record<string, unknown>;
  fieldErrors: FieldError[];
  prepopulatedIndicators: Set<number>;
  prepopSources: Record<number, string>;
  onFieldChange: (indicatorId: number, value: unknown) => void;
  getFieldValue: (indicatorId: number) => unknown;
}

function SectionForm({
  section,
  fieldErrors,
  prepopulatedIndicators,
  prepopSources,
  onFieldChange,
  getFieldValue,
}: SectionFormProps) {
  const errorMap = useMemo(() => {
    const map = new Map<number, string>();
    for (const err of fieldErrors) {
      map.set(err.indicatorId, err.message);
    }
    return map;
  }, [fieldErrors]);

  return (
    <div className="rounded-lg border border-ic-border bg-ic-card">
      <div className="border-b border-ic-border p-4">
        <h2 className="text-sm font-semibold text-ic-text-primary">{section.name}</h2>
        {section.description && (
          <p className="mt-1 text-xs text-ic-text-tertiary">{section.description}</p>
        )}
      </div>

      <div className="divide-y divide-ic-border/50 p-4">
        {section.indicators.map((indicator) => (
          <IndicatorField
            key={indicator.id}
            indicator={indicator}
            value={getFieldValue(indicator.id)}
            error={errorMap.get(indicator.id)}
            isPrepopulated={prepopulatedIndicators.has(indicator.id)}
            prepopSource={prepopSources[indicator.id]}
            onChange={(val) => onFieldChange(indicator.id, val)}
          />
        ))}
      </div>
    </div>
  );
}

// ─── Indicator Field ─────────────────────────────────────────────

interface IndicatorFieldProps {
  indicator: TemplateIndicator;
  value: unknown;
  error?: string;
  isPrepopulated: boolean;
  prepopSource?: string;
  onChange: (value: unknown) => void;
}

function IndicatorField({
  indicator,
  value,
  error,
  isPrepopulated,
  prepopSource,
  onChange,
}: IndicatorFieldProps) {
  const [showTooltip, setShowTooltip] = useState(false);
  const isRequired = indicator.required ?? indicator.validation?.required ?? false;

  return (
    <div className="py-3 first:pt-0 last:pb-0">
      {/* Label Row */}
      <div className="flex items-center gap-2 mb-1.5">
        <label className="text-xs font-medium text-ic-text-primary">
          {indicator.name}
          {isRequired && <span className="ml-0.5 text-red-400">*</span>}
        </label>
        {indicator.code && (
          <span className="font-mono text-[10px] text-ic-text-tertiary">{indicator.code}</span>
        )}
        {indicator.description && (
          <div className="relative">
            <button
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onFocus={() => setShowTooltip(true)}
              onBlur={() => setShowTooltip(false)}
              className="text-ic-text-tertiary hover:text-ic-text-secondary"
              type="button"
              aria-label="Show indicator description"
            >
              <Info className="h-3 w-3" />
            </button>
            {showTooltip && (
              <div className="absolute bottom-full left-1/2 z-20 mb-1 w-56 -translate-x-1/2 rounded-md border border-ic-border bg-ic-card p-2 text-[10px] text-ic-text-secondary shadow-lg">
                {indicator.description}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Prepopulated notice */}
      {isPrepopulated && prepopSource && (
        <p className="mb-1.5 text-[10px] text-blue-400">
          Pre-populated from {prepopSource}
        </p>
      )}

      {/* Input Field */}
      <div className="max-w-md">
        {indicator.type === 'number' && (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={value !== undefined && value !== null && value !== '' ? String(value) : ''}
              onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
              min={indicator.validation?.min}
              max={indicator.validation?.max}
              className={clsx(
                'w-full rounded-md border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary/50 focus:outline-none focus:ring-1',
                error
                  ? 'border-red-500/50 focus:ring-red-500/50'
                  : 'border-ic-border focus:ring-ic-accent/50',
              )}
              placeholder={
                indicator.validation?.min !== undefined && indicator.validation?.max !== undefined
                  ? `${indicator.validation.min} - ${indicator.validation.max}`
                  : 'Enter value'
              }
            />
            {indicator.unit && (
              <span className="shrink-0 text-xs text-ic-text-tertiary">{indicator.unit}</span>
            )}
          </div>
        )}

        {indicator.type === 'percentage' && (
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={value !== undefined && value !== null && value !== '' ? String(value) : ''}
              onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
              min={0}
              max={100}
              step={0.1}
              className={clsx(
                'w-full rounded-md border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary/50 focus:outline-none focus:ring-1',
                error
                  ? 'border-red-500/50 focus:ring-red-500/50'
                  : 'border-ic-border focus:ring-ic-accent/50',
              )}
              placeholder="0 - 100"
            />
            <span className="shrink-0 text-xs text-ic-text-tertiary">%</span>
          </div>
        )}

        {indicator.type === 'text' && (
          <input
            type="text"
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            className={clsx(
              'w-full rounded-md border bg-ic-input px-3 py-2 text-xs text-ic-text-primary placeholder:text-ic-text-tertiary/50 focus:outline-none focus:ring-1',
              error
                ? 'border-red-500/50 focus:ring-red-500/50'
                : 'border-ic-border focus:ring-ic-accent/50',
            )}
            placeholder="Enter value"
          />
        )}

        {indicator.type === 'boolean' && (
          <label className="inline-flex cursor-pointer items-center gap-2">
            <div className="relative">
              <input
                type="checkbox"
                checked={Boolean(value)}
                onChange={(e) => onChange(e.target.checked)}
                className="peer sr-only"
              />
              <div className="h-5 w-9 rounded-full bg-slate-700 transition-colors peer-checked:bg-ic-accent peer-focus-visible:ring-2 peer-focus-visible:ring-ic-accent/50" />
              <div className="absolute left-0.5 top-0.5 h-4 w-4 rounded-full bg-white transition-transform peer-checked:translate-x-4" />
            </div>
            <span className="text-xs text-ic-text-secondary">
              {value ? 'Yes' : 'No'}
            </span>
          </label>
        )}

        {indicator.type === 'select' && (
          <select
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            className={clsx(
              'w-full rounded-md border bg-ic-input px-3 py-2 text-xs text-ic-text-primary focus:outline-none focus:ring-1',
              error
                ? 'border-red-500/50 focus:ring-red-500/50'
                : 'border-ic-border focus:ring-ic-accent/50',
            )}
          >
            <option value="">Select...</option>
            {(indicator.validation?.options ?? []).map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Validation Error */}
      {error && (
        <div className="mt-1 flex items-center gap-1 text-[10px] text-red-400">
          <AlertCircle className="h-3 w-3" />
          {error}
        </div>
      )}
    </div>
  );
}

// ─── Review Step ─────────────────────────────────────────────────

interface ReviewStepProps {
  sections: TemplateSection[];
  formData: Record<string, unknown>;
  prepopulatedIndicators: Set<number>;
  getFieldValue: (indicatorId: number) => unknown;
  fieldErrors: FieldError[];
}

function ReviewStep({ sections, prepopulatedIndicators, getFieldValue, fieldErrors }: ReviewStepProps) {
  const hasErrors = fieldErrors.length > 0;

  return (
    <div className="space-y-4">
      {hasErrors && (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4">
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-red-400" />
            <span className="text-xs font-medium text-red-400">
              {fieldErrors.length} validation error{fieldErrors.length !== 1 ? 's' : ''} found. Please fix before submitting.
            </span>
          </div>
        </div>
      )}

      {sections.map((section) => (
        <div key={section.id} className="rounded-lg border border-ic-border bg-ic-card">
          <div className="border-b border-ic-border px-4 py-3">
            <h3 className="text-xs font-semibold uppercase tracking-wider text-ic-text-tertiary">
              {section.name}
            </h3>
          </div>
          <div className="divide-y divide-ic-border/50">
            {section.indicators.map((indicator) => {
              const value = getFieldValue(indicator.id);
              const displayValue = formatDisplayValue(indicator, value);
              const isPrepop = prepopulatedIndicators.has(indicator.id);

              return (
                <div key={indicator.id} className="flex items-center justify-between px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-ic-text-primary">{indicator.name}</span>
                    {indicator.code && (
                      <span className="font-mono text-[10px] text-ic-text-tertiary">{indicator.code}</span>
                    )}
                    {isPrepop && (
                      <span className="rounded bg-blue-500/10 px-1.5 py-0.5 text-[9px] text-blue-400">
                        pre-populated
                      </span>
                    )}
                  </div>
                  <span className={clsx(
                    'font-mono text-xs',
                    displayValue === '--' ? 'text-ic-text-tertiary' : 'text-ic-text-primary',
                  )}>
                    {displayValue}
                    {indicator.unit && displayValue !== '--' && (
                      <span className="ml-1 text-ic-text-tertiary">{indicator.unit}</span>
                    )}
                    {indicator.type === 'percentage' && displayValue !== '--' && (
                      <span className="text-ic-text-tertiary">%</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────

function formatDisplayValue(indicator: TemplateIndicator, value: unknown): string {
  if (value === undefined || value === null || value === '') return '--';

  switch (indicator.type) {
    case 'boolean':
      return value ? 'Yes' : 'No';
    case 'number':
    case 'percentage':
      return typeof value === 'number' ? value.toLocaleString() : String(value);
    default:
      return String(value);
  }
}
